import { useCallback } from 'react';

import { useAppLanguage } from '@/context/appLanguage';
import { useOrganization } from '@/context/organization/useOrganization';
import {
  captureException as sentryCaptureException,
  captureMessage as sentryCaptureMessage,
  type SeverityLevel,
  withScope,
} from '@/lib/sentry';

type SentryContext = {
  extras?: Record<string, unknown>;
  level?: SeverityLevel;
  tags?: Record<string, string>;
};

/**
 * Custom hook for capturing exceptions and messages to Sentry with automatic context.
 * Automatically includes organization and language context in all captured events.
 *
 * @returns Object with captureException and captureMessage methods.
 *
 * @example
 * ```typescript
 * const { captureException } = useSentry();
 *
 * try {
 *   await riskyOperation();
 * } catch (error) {
 *   captureException(error, {
 *     tags: { component: 'MyComponent' },
 *     extras: { formData: values },
 *   });
 * }
 * ```
 */
export function useSentry() {
  const { organizationId, organization } = useOrganization();
  const { appLanguage } = useAppLanguage();

  /**
   * Captures an exception to Sentry with automatic context.
   *
   * @param error - The error to capture.
   * @param context - Additional context (tags, extras, level).
   *
   * @example
   * ```typescript
   * captureException(error, {
   *   tags: { component: 'SermonsUploadForm' },
   *   extras: { fileSize: 1024000 },
   * });
   * ```
   */
  const captureException = useCallback(
    (error: unknown, context?: SentryContext) => {
      withScope(scope => {
        // Automatically add common context
        scope.setExtras({
          organizationId,
          organizationName: organization?.name,
          appLanguage,
          ...context?.extras,
        });

        if (context?.tags) {
          scope.setTags(context.tags);
        }

        if (context?.level) {
          scope.setLevel(context.level);
        }

        sentryCaptureException(error);
      });
    },
    [organizationId, organization?.name, appLanguage]
  );

  /**
   * Captures a message to Sentry with automatic context.
   *
   * @param message - The message to capture.
   * @param context - Additional context (tags, extras, level).
   *
   * @example
   * ```typescript
   * captureMessage('Unexpected state reached', {
   *   level: 'warning',
   *   tags: { component: 'StateMachine' },
   * });
   * ```
   */
  const captureMessage = useCallback(
    (message: string, context?: SentryContext) => {
      withScope(scope => {
        scope.setExtras({
          organizationId,
          organizationName: organization?.name,
          appLanguage,
          ...context?.extras,
        });

        if (context?.tags) {
          scope.setTags(context.tags);
        }

        sentryCaptureMessage(message, context?.level || 'info');
      });
    },
    [organizationId, organization?.name, appLanguage]
  );

  return { captureException, captureMessage };
}
